import { useCallback } from 'react'

import { isCookieCategoryEnabled } from '../../consent'
import { CookieCategoryTypes } from '../../consent/types'
import { useConfig } from '../../context/config'
import {
  trackAddPaymentInfo,
  trackCheckout,
  trackConfirmAge,
  trackSelectItem,
  trackPageView,
  trackPayMethod,
  trackPurchase,
  trackScheduleItem,
  trackSign,
  trackSignUp,
  trackTutorialBegin,
  trackTutorialComplete,
  trackWarning,
} from '../../tracking'

export function useTracking() {
  const {
    config: { enableCookieConsent },
  } = useConfig()

  const isTrackingAllowed = () => {
    return !enableCookieConsent || isCookieCategoryEnabled(CookieCategoryTypes.PERFORMANCE_COOKIES)
  }

  const trackScheduleItemFn = useCallback((...args: Parameters<typeof trackScheduleItem>) => {
    if (isTrackingAllowed()) {
      trackScheduleItem(...args)
    }
  }, [])

  const trackPayMethodFn = useCallback(() => {
    if (isTrackingAllowed()) {
      trackPayMethod()
    }
  }, [])

  const trackSignFn = useCallback(() => {
    if (isTrackingAllowed()) {
      trackSign()
    }
  }, [])

  const trackTutorialCompleteFn = useCallback(() => {
    if (isTrackingAllowed()) {
      trackTutorialComplete()
    }
  }, [])

  const trackTutorialBeginFn = useCallback(() => {
    if (isTrackingAllowed()) {
      trackTutorialBegin()
    }
  }, [])

  const trackConfirmAgeFn = useCallback(() => {
    if (isTrackingAllowed()) {
      trackConfirmAge()
    }
  }, [])

  const trackWarningFn = useCallback(() => {
    if (isTrackingAllowed()) {
      trackWarning()
    }
  }, [])

  const trackAddPaymentInfoFn = useCallback((...args: Parameters<typeof trackAddPaymentInfo>) => {
    if (isTrackingAllowed()) {
      trackAddPaymentInfo(...args)
    }
  }, [])

  const trackCheckoutFn = useCallback((...args: Parameters<typeof trackCheckout>) => {
    if (isTrackingAllowed()) {
      trackCheckout(...args)
    }
  }, [])

  const trackSelectItemFn = useCallback((...args: Parameters<typeof trackSelectItem>) => {
    if (isTrackingAllowed()) {
      trackSelectItem(...args)
    }
  }, [])

  const trackPageViewFn = useCallback((...args: Parameters<typeof trackPageView>) => {
    if (isTrackingAllowed()) {
      trackPageView(...args)
    }
  }, [])

  const trackPurchaseFn = useCallback((...args: Parameters<typeof trackPurchase>) => {
    if (isTrackingAllowed()) {
      trackPurchase(...args)
    }
  }, [])

  const trackSignUpFn = useCallback((...args: Parameters<typeof trackSignUp>) => {
    if (isTrackingAllowed()) {
      trackSignUp(...args)
    }
  }, [])

  return {
    trackAddPaymentInfo: trackAddPaymentInfoFn,
    trackCheckout: trackCheckoutFn,
    trackConfirmAge: trackConfirmAgeFn,
    trackSelectItem: trackSelectItemFn,
    trackPageView: trackPageViewFn,
    trackPayMethod: trackPayMethodFn,
    trackPurchase: trackPurchaseFn,
    trackScheduleItem: trackScheduleItemFn,
    trackSign: trackSignFn,
    trackSignUp: trackSignUpFn,
    trackTutorialBegin: trackTutorialBeginFn,
    trackTutorialComplete: trackTutorialCompleteFn,
    trackWarning: trackWarningFn,
  }
}
